import React from 'react';
import { Box, Button, Card, Container, Grid, Typography } from '@material-ui/core';

const renderSlideshow = (sectionConfig = {}, currentSlideIndex = 0) => {
    if (!sectionConfig.slideshow?.enabled || !sectionConfig.slideshow?.images?.length) {
        return null;
    }

    return (
        <Box style={sectionConfig.slideshow?.containerStyles || {}}>
            {sectionConfig.slideshow.images.map((image, index) => (
                <img
                    key={`hero-slide-${index}`}
                    src={image.url}
                    alt={image.alt || `hero-slide-${index}`}
                    style={{
                        ...(sectionConfig.slideshow?.imageStyles || {}),
                        ...(index === currentSlideIndex ? sectionConfig.slideshow?.activeImageStyles || {} : sectionConfig.slideshow?.inactiveImageStyles || {})
                    }}
                />
            ))}
            <Box style={sectionConfig.slideshow?.overlayStyles || {}} />
        </Box>
    );
};

const floatingKeyframes = `
@keyframes heroFloat {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(3deg); }
}
@keyframes heroDrift {
    0%, 100% { transform: translate(0px, 0px); }
    33% { transform: translate(15px, -12px); }
    66% { transform: translate(-10px, -20px); }
}
@keyframes heroPulse {
    0%, 100% { opacity: 0.15; transform: scale(1); }
    50% { opacity: 0.25; transform: scale(1.05); }
}
`;

const renderBackgroundLayers = (layers = [], getIcon) => {
    if (!Array.isArray(layers) || !layers.length) {
        return null;
    }

    return (
        <>
            <style>{floatingKeyframes}</style>
            {layers.map((layer, index) => (
                <Box
                    key={`hero-bg-layer-${index}`}
                    className={layer.className || undefined}
                    style={layer.styles || {}}
                >
                    {layer.icon && getIcon?.(layer.icon, {
                        style: {
                            width: '100%',
                            height: '100%',
                            ...(layer.iconStyles || {})
                        }
                    })}
                </Box>
            ))}
        </>
    );
};

const PortalCard = ({ card = {}, layoutContext = {}, cardStyles = {} }) => {
    const { applyHoverEffect, removeHoverEffect, getIcon, handleHeroButtonClick } = layoutContext;

    const handleButtonClick = (buttonConfig) => {
        if (!buttonConfig) return;
        if (buttonConfig.action && layoutContext.functionMap?.[buttonConfig.action]) {
            layoutContext.functionMap[buttonConfig.action]();
            return;
        }
        if (buttonConfig.action === 'handleLoginClick') {
            layoutContext.handleLoginClick?.();
            return;
        }
        if (buttonConfig.action === 'handleSignUpClick') {
            layoutContext.handleSignUpClick?.();
            return;
        }
        handleHeroButtonClick?.(buttonConfig || {});
    };

    return (
        <Card
            style={{
                ...(cardStyles.cardStyles || {}),
                ...(card.cardStyles || {})
            }}
            onMouseEnter={(e) => applyHoverEffect?.(e, cardStyles.cardHoverStyles || card.cardHoverStyles || {})}
            onMouseLeave={(e) => removeHoverEffect?.(e, { ...(cardStyles.cardStyles || {}), ...(card.cardStyles || {}) })}
        >
            {card.banner && (
                <Box style={{
                    ...(cardStyles.bannerStyles || {}),
                    ...(card.banner.styles || {})
                }}>
                    {card.banner.icon && getIcon?.(card.banner.icon, {
                        style: {
                            ...(cardStyles.bannerIconStyles || {}),
                            ...(card.banner.iconStyles || {})
                        }
                    })}
                </Box>
            )}

            {!card.banner && (
                <Box style={card.headerStyles || cardStyles.headerStyles || {}}>
                    {card.icon && (
                        <Box style={{
                            ...(cardStyles.iconContainerStyles || {}),
                            ...(card.iconContainerStyles || {}),
                            background: card.iconBackground || cardStyles.iconContainerStyles?.background || 'linear-gradient(135deg, #003B5C, #0284c7)'
                        }}>
                            {getIcon?.(card.icon, {
                                style: {
                                    ...(cardStyles.iconStyles || {}),
                                    ...(card.iconStyles || {})
                                }
                            })}
                        </Box>
                    )}
                    <Box>
                        <Typography
                            variant={card.titleVariant || 'h5'}
                            style={{
                                ...(cardStyles.titleStyles || {}),
                                ...(card.titleStyles || {})
                            }}
                        >
                            {card.title}
                        </Typography>
                        {card.subtitle && (
                            <Typography
                                variant="body2"
                                style={{
                                    ...(cardStyles.subtitleStyles || {}),
                                    ...(card.subtitleStyles || {})
                                }}
                            >
                                {card.subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            <Box style={card.contentStyles || cardStyles.contentStyles || {}}>
                {card.banner && (
                    <>
                        <Typography
                            variant={card.titleVariant || 'h5'}
                            style={{
                                ...(cardStyles.titleStyles || {}),
                                ...(card.titleStyles || {})
                            }}
                        >
                            {card.title}
                        </Typography>
                        {card.subtitle && (
                            <Typography
                                variant="body2"
                                style={{
                                    ...(cardStyles.subtitleStyles || {}),
                                    ...(card.subtitleStyles || {})
                                }}
                            >
                                {card.subtitle}
                            </Typography>
                        )}
                    </>
                )}

                {Array.isArray(card.features) && card.features.length > 0 && (
                    <Box style={card.featuresContainerStyles || cardStyles.featuresContainerStyles || {}}>
                        {card.features.map((feature, featureIndex) => (
                            <Box
                                key={`feature-${featureIndex}`}
                                style={{
                                    ...(cardStyles.featureItemStyles || {}),
                                    ...(feature.styles || {})
                                }}
                            >
                                {feature.icon && (
                                    <Box
                                        component="span"
                                        style={{
                                            ...(cardStyles.featureIconWrapperStyles || {}),
                                            ...(feature.iconWrapperStyles || {})
                                        }}
                                    >
                                        {getIcon?.(feature.icon, {
                                            style: {
                                                ...(cardStyles.featureIconStyles || {}),
                                                ...(feature.iconStyles || {})
                                            }
                                        })}
                                    </Box>
                                )}
                                <Typography
                                    variant="body2"
                                    component="span"
                                    style={{
                                        ...(cardStyles.featureTextStyles || {}),
                                        ...(feature.textStyles || {})
                                    }}
                                >
                                    {feature.text}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {(card.registerButton || card.button) && (
                    <Box style={card.buttonContainerStyles || cardStyles.buttonContainerStyles || {}}>
                        <Box style={{ display: 'flex', flexDirection: 'column', gap: (card.buttonGap || 12) }}>
                            {card.registerButton && (
                                <Button
                                    variant={card.registerButton.variant || 'contained'}
                                    fullWidth={card.registerButton.fullWidth !== false}
                                    style={card.registerButton.styles || cardStyles.buttonStyles || {}}
                                    onMouseEnter={(e) => applyHoverEffect?.(e, card.registerButton.hoverStyles || cardStyles.buttonHoverStyles || {})}
                                    onMouseLeave={(e) => removeHoverEffect?.(e, card.registerButton.styles || cardStyles.buttonStyles || {})}
                                    onClick={() => handleButtonClick(card.registerButton)}
                                    startIcon={card.registerButton.icon ? getIcon?.(card.registerButton.icon, { style: { fontSize: '1.1rem' } }) : null}
                                >
                                    {card.registerButton.text}
                                </Button>
                            )}

                            {card.button && (
                                <Button
                                    variant={card.button.variant || 'contained'}
                                    fullWidth={card.button.fullWidth !== false}
                                    style={card.button.styles || cardStyles.buttonStyles || {}}
                                    onMouseEnter={(e) => applyHoverEffect?.(e, card.button.hoverStyles || cardStyles.buttonHoverStyles || {})}
                                    onMouseLeave={(e) => removeHoverEffect?.(e, card.button.styles || cardStyles.buttonStyles || {})}
                                    onClick={() => handleButtonClick(card.button)}
                                    startIcon={card.button.icon ? getIcon?.(card.button.icon, { style: { fontSize: '1.1rem' } }) : null}
                                >
                                    {card.button.text}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

const HeroPortalCards = ({ sectionConfig = {}, layoutContext = {}, globalConfig = {} }) => {
    const {
        applyHoverEffect,
        removeHoverEffect,
        handleHeroButtonClick,
        getIcon,
        getAnimationProps,
        currentSlideIndex
    } = layoutContext;

    const applyAnimation = (animationConfig, index = 0) => (
        typeof getAnimationProps === 'function' ? getAnimationProps(animationConfig, index) : {}
    );

    const heroTitleText = sectionConfig.title?.text ?? globalConfig.title;
    const heroSubtitleText = sectionConfig.subtitle?.text ?? globalConfig.subtitle;
    const heroDescriptionText = sectionConfig.description?.text ?? globalConfig.description;
    const subtitlePosition = sectionConfig.subtitle?.position || 'afterTitle';

    const sectionAnimation = applyAnimation(sectionConfig.animation?.section);
    const containerAnimation = applyAnimation(sectionConfig.animation?.container);
    const contentAnimation = applyAnimation(sectionConfig.animation?.content);
    const cardsAnimation = applyAnimation(sectionConfig.animation?.cards);

    const portalCards = sectionConfig.portalCards || [];

    return (
        <Box
            id="hero-section"
            className={sectionAnimation.className}
            data-anim={sectionAnimation.dataAnim}
            data-delay={sectionAnimation.dataDelay}
            data-trigger-once={sectionAnimation.dataTriggerOnce}
            style={{ ...(sectionConfig.styles || {}), ...(sectionAnimation.style || {}) }}
        >
            {renderBackgroundLayers(sectionConfig.backgroundLayers, getIcon)}
            {renderSlideshow(sectionConfig, currentSlideIndex)}
            <Container
                maxWidth={sectionConfig.container?.maxWidth || 'lg'}
                style={{
                    ...(sectionConfig.containerStyles || {}),
                    ...(containerAnimation.style || {}),
                    position: 'relative',
                    zIndex: 2
                }}
                className={containerAnimation.className}
                data-anim={containerAnimation.dataAnim}
                data-delay={containerAnimation.dataDelay}
                data-trigger-once={containerAnimation.dataTriggerOnce}
            >
                <Grid
                    container
                    spacing={sectionConfig.grid?.spacing || 6}
                    alignItems={sectionConfig.grid?.alignItems || 'center'}
                >
                    <Grid
                        item
                        xs={sectionConfig.gridItem?.xs || 12}
                        lg={sectionConfig.gridItem?.lg || 6}
                    >
                        <Box
                            className={contentAnimation.className}
                            data-anim={contentAnimation.dataAnim}
                            data-delay={contentAnimation.dataDelay}
                            data-trigger-once={contentAnimation.dataTriggerOnce}
                            style={contentAnimation.style || {}}
                        >
                            {subtitlePosition === 'beforeTitle' && heroSubtitleText && (
                                <Typography
                                    variant={sectionConfig.typography?.subtitle?.variant || 'h4'}
                                    style={sectionConfig.subtitle?.styles || {}}
                                >
                                    {heroSubtitleText}
                                </Typography>
                            )}
                            <Typography
                                variant={sectionConfig.typography?.title?.variant || 'h1'}
                                style={sectionConfig.title?.styles || {}}
                            >
                                {heroTitleText}
                            </Typography>
                            {subtitlePosition !== 'beforeTitle' && heroSubtitleText && (
                                <Typography
                                    variant={sectionConfig.typography?.subtitle?.variant || 'h4'}
                                    style={sectionConfig.subtitle?.styles || {}}
                                >
                                    {heroSubtitleText}
                                </Typography>
                            )}
                            {heroDescriptionText && (
                                <Typography
                                    variant={sectionConfig.typography?.description?.variant || 'h6'}
                                    style={sectionConfig.description?.styles || {}}
                                >
                                    {heroDescriptionText}
                                </Typography>
                            )}
                            {Array.isArray(sectionConfig.buttons) && sectionConfig.buttons.length > 0 && (
                                <Box style={sectionConfig.buttonGroupStyles || {}}>
                                    {sectionConfig.buttons.map((buttonConfig, buttonIndex) => {
                                        const iconElement = buttonConfig.icon ? getIcon?.(buttonConfig.icon, { style: { fontSize: '1rem' } }) : null;
                                        const isEndIcon = buttonConfig.iconPosition === 'end';
                                        return (
                                            <Button
                                                key={`hero-btn-${buttonIndex}`}
                                                variant={buttonConfig.variant || 'contained'}
                                                color={buttonConfig.color || 'primary'}
                                                style={buttonConfig.styles || {}}
                                                onMouseEnter={(e) => applyHoverEffect?.(e, buttonConfig.hoverStyles || {})}
                                                onMouseLeave={(e) => removeHoverEffect?.(e, buttonConfig.styles || {})}
                                                onClick={() => handleHeroButtonClick?.(buttonConfig)}
                                                startIcon={!isEndIcon ? iconElement : null}
                                                endIcon={isEndIcon ? iconElement : null}
                                            >
                                                {buttonConfig.text}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid
                        item
                        xs={sectionConfig.gridItemCards?.xs || 12}
                        lg={sectionConfig.gridItemCards?.lg || 6}
                    >
                        {sectionConfig.portalCardsTitle?.text && (
                            <Typography
                                variant="h5"
                                style={sectionConfig.portalCardsTitle?.styles || {}}
                            >
                                {sectionConfig.portalCardsTitle.text}
                            </Typography>
                        )}
                        <Box
                            className={cardsAnimation.className}
                            data-anim={cardsAnimation.dataAnim}
                            data-delay={cardsAnimation.dataDelay}
                            data-trigger-once={cardsAnimation.dataTriggerOnce}
                            style={{
                                ...(sectionConfig.portalCardsContainerStyles || {}),
                                ...(cardsAnimation.style || {})
                            }}
                        >
                            {portalCards.map((card, cardIndex) => (
                                <PortalCard
                                    key={`portal-card-${cardIndex}`}
                                    card={card}
                                    layoutContext={layoutContext}
                                    cardStyles={sectionConfig.portalCardStyles || {}}
                                />
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default HeroPortalCards;
